import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ChefHat } from 'lucide-react';

export const BeefAndBroccoli: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Classic Beef and Broccoli Recipe | Chinese Food Maps</title>
        <meta name="description" content="Master the art of making restaurant-style beef and broccoli at home. Learn the secrets to tender beef and perfectly cooked broccoli in a rich brown sauce." />
        <meta property="og:title" content="Classic Beef and Broccoli Recipe | Chinese Food Maps" />
        <meta property="og:description" content="Master the art of making restaurant-style beef and broccoli at home." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://chinesefoodmaps.com/recipes/beef-and-broccoli" />
        <meta property="og:image" content="https://cdn-icons-png.flaticon.com/512/2548/2548242.png" />
        <link rel="canonical" href="https://chinesefoodmaps.com/recipes/beef-and-broccoli" />
      </Helmet>
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-6 text-red-900">Classic Beef and Broccoli Recipe</h1>
        
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="prose max-w-none text-gray-600">
            <p className="text-lg leading-relaxed mb-6">
              Beef and Broccoli is a beloved Chinese-American dish known for its tender slices of beef and crisp broccoli in a savory brown sauce. This recipe will help you recreate this takeout favorite at home.
            </p>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Beef Marinade:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>1 lb flank steak, sliced against the grain</li>
              <li>1 tbsp soy sauce</li>
              <li>1 tbsp Chinese cooking wine (Shaoxing)</li>
              <li>1 tsp cornstarch</li>
              <li>1 tsp baking soda (for tenderizing)</li>
              <li>1 tsp vegetable oil</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">For the Sauce:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>½ cup oyster sauce</li>
              <li>¼ cup soy sauce</li>
              <li>¼ cup Chinese cooking wine</li>
              <li>2 tbsp dark soy sauce</li>
              <li>2 tbsp brown sugar</li>
              <li>1 cup chicken stock</li>
              <li>2 tbsp cornstarch</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Additional Ingredients:</h3>
            <ul className="list-disc pl-6 mb-6 space-y-2">
              <li>2 large broccoli crowns, cut into florets</li>
              <li>4 cloves garlic, minced</li>
              <li>1-inch ginger, minced</li>
              <li>2 tbsp vegetable oil</li>
              <li>Sesame oil for finishing</li>
            </ul>

            <h3 className="text-xl font-semibold text-red-800 mb-4">Instructions:</h3>
            <ol className="list-decimal pl-6 mb-6 space-y-4">
              <li>
                <strong>Marinate the Beef:</strong> Combine beef with marinade ingredients. Let sit for 30 minutes.
              </li>
              <li>
                <strong>Prepare the Sauce:</strong> Mix all sauce ingredients in a bowl. Set aside.
              </li>
              <li>
                <strong>Blanch Broccoli:</strong> Cook broccoli in boiling water for 2 minutes. Shock in ice water.
              </li>
              <li>
                <strong>Cook Beef:</strong> Stir-fry beef in batches until browned. Remove and set aside.
              </li>
              <li>
                <strong>Combine:</strong> Stir-fry garlic and ginger, add sauce, return beef and broccoli.
              </li>
              <li>
                <strong>Finish:</strong> Simmer until sauce thickens. Drizzle with sesame oil.
              </li>
            </ol>

            <div className="bg-yellow-50 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-red-800 mb-2">Pro Tips from Chinese Food Maps:</h4>
              <ul className="list-disc pl-6 space-y-2">
                <li>Freeze beef for 30 minutes before slicing for easier cutting</li>
                <li>Don't skip the baking soda - it's key for tender beef</li>
                <li>Blanch broccoli separately to maintain its bright color</li>
                <li>Use high heat and don't overcrowd the wok</li>
                <li>Let the beef rest at room temperature before cooking</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};